// Forma Wizard para la forma de llenado de usuarios nuevo (empleado)

import React, { useState, useEffect } from 'react';

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Grid,
  MenuItem,
  TextField,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  FIELD_LABELS,
  curpValidator,
  rfcValidator,
  emailValidator,
  nameValidator,
  streetNameValidator,
  zipCodeValidator,
  passwordValidator,
  phoneValidator,
  requiredValidator,
  validateField,
  numbersOnlyValidator,
} from '../validators';
import { MEXICAN_STATES } from '../states';
import { getAddressInfoForZipCode } from '../sepomex.js';
import { getUserTypes } from './UserType.js';

const steps = ['Usuario', 'Datos personales', 'Informacion de contacto', 'Informacion de laboral'];

export default function EmployeeUserWizardForm({
  employee = null,
  onSave,
  onCancel,
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({ contactPerson: {} });
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [form, setForm] = useState({
    userId: '',
    email: '',
    password: '',
    active: true,

    firstname: '',
    lastnameFirst: '',
    lastnameSecond: '',
    phoneNumber: '',
    mobileNumber: '',
    maritalStatus: 'SINGLE',
    birthDate: '',

    street: '',
    neighborhood: '',
    municipality: '',
    city: '',
    state: '',
    zipCode: '',
    role: 'EMPLOYEE',
    rfc: '',
    curp: '',
    salary: 0.00,

    contactPerson: {
      firstname: '',
      lastnameFirst: '',
      lastnameSecond: '',
      email: '',
      phoneNumber: '',
      mobileNumber: '',
    }
  });

  useEffect(() => {
    if (employee) {
      setNeighborhoods(employee.address?.neighborhood ? [employee.address.neighborhood] : []);
      setForm({
        userId: employee.userId ?? '',
        email: employee.user.email ?? '',
        password: '',
        active: employee.active ?? true,
        rfc: employee.rfc ?? '',
        curp: employee.curp ?? '',
        salary: employee.salary ?? 0.00,
        personId: employee.user?.person?.id ?? '',
        firstname: employee.user?.person?.firstname ?? '',
        lastnameFirst: employee.user?.person?.lastnameFirst ?? '',
        lastnameSecond: employee.user?.person?.lastnameSecond ?? '',
        phoneNumber: employee.user?.person?.phoneNumber ?? '',
        mobileNumber: employee.user?.person?.mobileNumber ?? '',
        maritalStatus: employee.user?.person?.maritalStatus ?? 'SINGLE',
        birthDate: employee.user?.person?.birthDate ?? '',
        addressId: employee.address?.id ?? '',
        street: employee.address?.street ?? '',
        neighborhood: employee.address?.neighborhood ?? '',
        city: employee.address?.city ?? '',
        municipality: employee.address?.municipality ?? '',
        state: employee.address?.state ?? '',
        zipCode: employee.address?.zipCode ?? '',
        role: employee.user?.role ?? 'employee',
        contactPerson: {
          id: employee.contactPerson?.id ?? '',
          firstname: employee.contactPerson?.firstname ?? '',
          lastnameFirst: employee.contactPerson?.lastnameFirst ?? '',
          lastnameSecond: employee.contactPerson?.lastnameSecond ?? '',
          email: employee.contactPerson?.email ?? '',
          phoneNumber: employee.contactPerson?.phoneNumber ?? '',
          mobileNumber: employee.contactPerson?.mobileNumber ?? '',
        }
      });
    }
  }, [employee]);

  const setDeepValue = (obj, path, value) => {
    const keys = path.split('.');
    const lastKey = keys.pop();

    const newObj = { ...obj };
    let pointer = newObj;

    keys.forEach(key => {
      pointer[key] = { ...pointer[key] };
      pointer = pointer[key];
    });

    pointer[lastKey] = value;
    return newObj;
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setForm(prev => setDeepValue(prev, name, newValue));
    setErrors(prev => {
      const next = { ...prev };
      let error = null;

      if (name === 'email' || name === 'contactPerson.email') {
        error = validateField(newValue, [
          requiredValidator,
          emailValidator
        ]);
      }

      if (name === 'rfc') {
        error = validateField(newValue, [
          requiredValidator,
          rfcValidator
        ]);
      }

      if (name === 'curp') {
        error = validateField(newValue, [
          requiredValidator,
          curpValidator
        ]);
      }

      if (name === 'password' && !employee) {
        error = validateField(newValue, [
          passwordValidator
        ]);
      }

      if (name === 'street') {
        error = validateField(newValue, [
          requiredValidator,
          streetNameValidator
        ]);
      }

      if (name === 'firstname' || name === 'lastnameFirst' || name === 'lastnameSecond' || name === 'municipality' || name === 'city' || name === 'contactPerson.firstname'
        || name === 'contactPerson.lastnameFirst' || name === 'contactPerson.lastnameSecond' || name === 'neighborhood') {
        error = validateField(newValue, [
          requiredValidator,
          (v) => nameValidator(v, FIELD_LABELS[name])
        ]);
      }

      if (name === 'zipCode') {
        error = validateField(value, [
          requiredValidator,
          zipCodeValidator
        ]);
      }

      if (name === 'salary') {
        error = validateField(value, [
          requiredValidator,
          numbersOnlyValidator
        ]);
      }

      if (name === 'phoneNumber' || name === 'contactPerson.phoneNumber') {
        error = validateField(newValue, [
          requiredValidator,
          phoneValidator
        ]);
      }

      if (name === 'mobileNumber' || name === 'contactPerson.mobileNumber') {
        error = validateField(newValue, [
          requiredValidator,
          phoneValidator
        ]);
      }

      if (name === 'zipCode' && !error && value.length === 5) {
        // Solo llamar a SEPOMEX si el código postal es válido y completo (5 dígitos).
        setTimeout(() => {
          getAddressInfoForZipCode(value).then((data) => {
            if (data) {
              if (data.city) {
                setForm((prev) => ({ ...prev, city: data.city }));
              }
              if (data.state) {
                setForm((prev) => ({ ...prev, state: data.state }));
              }
              if (data.municipality) {
                setForm((prev) => ({ ...prev, municipality: data.municipality }));
              }
              if (data.neighborhoods.length > 0) {
                setNeighborhoods(data.neighborhoods);
                setForm((prev) => ({ ...prev, neighborhood: data.neighborhoods[0] }));
              }
            }
          });
        });
      }

      if (error) {
        return setDeepValue(prev, name, error);
      } else {
        return setDeepValue(prev, name, undefined);
      }
    });
  };

  const handleSubmit = () => {
    onSave(form);
  };

  const stepFields = {
    0: ['email', 'password', 'role'],
    1: ['firstname', 'lastnameFirst', 'lastnameSecond', 'phoneNumber', 'maritalStatus', 'street',
      'neighborhood', 'state', 'municipality', 'city', 'birthDate'],
    2: ['contactPerson.firstname', 'contactPerson.lastnameFirst', 'contactPerson.lastnameSecond',
      'contactPerson.email', 'contactPerson.phoneNumber', 'contactPerson.mobileNumber'],
    3: ['curp', 'rfc', 'salary'],
  };

  const isStepValid = (step = activeStep) => {
    const fields = stepFields[step] || [];

    const hasStepErrors = fields.some(f => errors[f]);
    if (hasStepErrors) return false;

    if (step === 0) {
      return form.email && (employee || form.password);
    }
    if (step === 1) {
      return form.firstname && form.lastnameFirst && form.phoneNumber && form.mobileNumber && form.street
        && form.neighborhood && form.state && form.municipality && form.city;
    }
    if (step === 2) {
      return form.contactPerson.firstname && form.contactPerson.lastnameFirst
        && form.contactPerson.email && form.contactPerson.mobileNumber && form.contactPerson.phoneNumber;
    }
    if (step === 3) {
      return form.rfc && form.curp;
    }
    return true;
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid
            container
            spacing={2}>
            <Grid item size={{ xs: 12, md: 6, lg: 3 }}>


              <TextField
                label="Email"
                name="email"
                error={!!errors.email}
                helperText={errors.email}
                value={form.email}
                onChange={handleChange}
                autoComplete="off"
                slotProps={{ htmlInput: { maxLength: 100 } }}
                fullWidth
                required
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                label="Contraseña"
                name="password"
                // type="password"
                autoComplete="new-password"
                value={form.password}
                onChange={handleChange}
                fullWidth
                required={!employee}
                slotProps={{ htmlInput: { maxLength: 15 } }}
                error={!!errors.password}
                helperText={
                  errors.password
                    ? (
                      Array.isArray(errors.password)
                        ? errors.password.join(' • ')
                        : errors.password
                    )
                    :
                    employee
                      ? 'Dejar vacío para no cambiarla'
                      : 'La contraseña cumple los requisitos de seguridad'
                }
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 6, lg: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="active"
                    checked={form.active}
                    onChange={handleChange}
                  />
                }
                label="Usuario activo"
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                select
                label="Rol"
                name="role"
                value={form.role}
                onChange={handleChange}
                fullWidth>
                {
                  getUserTypes().map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))
                }

              </TextField>

            </Grid>
          </Grid>
        );

      case 1:
        return (
          // fila 1
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                label="Nombre"
                name="firstname"
                value={form.firstname}
                onChange={handleChange}
                error={!!errors.firstname}
                helperText={errors.firstname}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                fullWidth
                required
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                label="Primer Apellido"
                name="lastnameFirst"
                value={form.lastnameFirst}
                onChange={handleChange}
                error={!!errors.lastnameFirst}
                helperText={errors.lastnameFirst}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                fullWidth
                required
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                label="Segundo Apellido"
                name="lastnameSecond"
                value={form.lastnameSecond}
                onChange={handleChange}
                error={!!errors.lastnameSecond}
                helperText={errors.lastnameSecond}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                fullWidth
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                label="Fecha de nacimiento"
                name="birthDate"
                type="date"
                value={form.birthDate}
                onChange={handleChange}
                fullWidth
                required
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                select
                label="Estado Civil"
                name="maritalStatus"
                value={form.maritalStatus}
                onChange={handleChange}
                fullWidth
                required
              >
                <MenuItem value="SINGLE">Soltero(a)</MenuItem>
                <MenuItem value="MARRIED">Casado(a)</MenuItem>
                <MenuItem value="WIDOW">Viudo(a)</MenuItem>
                <MenuItem value="DIVORCED">Divorciado(a)</MenuItem>
                <MenuItem value="COHABITATION">Concubinato</MenuItem>
              </TextField>
            </Grid>

            <Grid item size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                label="Calle"
                name="street"
                value={form.street}
                onChange={handleChange}
                error={!!errors.street}
                helperText={errors.street}
                slotProps={{ htmlInput: { maxLength: 255 } }}
                fullWidth
                required
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                label="Código Postal"
                name="zipCode"
                value={form.zipCode}
                onChange={handleChange}
                error={!!errors.zipCode}
                helperText={errors.zipCode}
                slotProps={{ htmlInput: { maxLength: 5 } }}
                fullWidth
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              {neighborhoods.length > 0 ? (
                <TextField
                  select
                  label="Colonia"
                  name="neighborhood"
                  value={form.neighborhood}
                  onChange={handleChange}
                  error={!!errors.neighborhood}
                  helperText={errors.neighborhood}
                  fullWidth
                  required
                >
                  <MenuItem value="">
                    <em>Seleccionar colonia</em>
                  </MenuItem>

                  {neighborhoods.map((colonia) => (
                    <MenuItem key={colonia} value={colonia}>
                      {colonia}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  label="Colonia"
                  name="neighborhood"
                  value={form.neighborhood}
                  onChange={handleChange}
                  error={!!errors.neighborhood}
                  helperText={
                    errors.neighborhood ||
                    'Escribe la colonia manualmente'
                  }
                  slotProps={{ htmlInput: { maxLength: 100 } }}
                  fullWidth
                  required
                />
              )}
            </Grid>

            <Grid item size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                label="Alcaldía"
                name="municipality"
                value={form.municipality}
                onChange={handleChange}
                error={!!errors.municipality}
                helperText={errors.municipality}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                fullWidth
                required
              // disabled={!form.zipCode || neighborhoods.length === 0}
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                label="Ciudad"
                name="city"
                value={form.city}
                onChange={handleChange}
                error={!!errors.city}
                helperText={errors.city}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                fullWidth
                required
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                select
                label="Estado"
                name="state"
                value={form.state}
                onChange={handleChange}
                fullWidth
                required
              >
                {MEXICAN_STATES.map((state) => (
                  <MenuItem key={state.code} value={state.name}>
                    {state.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                label="Teléfono móvil"
                name="mobileNumber"
                error={!!errors.mobileNumber}
                helperText={errors.mobileNumber}
                value={form.mobileNumber}
                onChange={handleChange}
                slotProps={{ htmlInput: { maxLength: 13 } }}
                fullWidth
                required
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                label="Teléfono domicilio"
                name="phoneNumber"
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
                value={form.phoneNumber}
                onChange={handleChange}
                slotProps={{ htmlInput: { maxLength: 13 } }}
                fullWidth
                required
              />
            </Grid>
          </Grid >
        );

      case 2: // persona de contacto
        return (
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                label="Nombre del persona de contacto"
                name="contactPerson.firstname"
                value={form.contactPerson.firstname}
                onChange={handleChange}
                error={!!errors.contactPerson.firstname}
                helperText={errors.contactPerson.firstname}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                fullWidth
                required
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                label="Primer Apellido"
                name="contactPerson.lastnameFirst"
                value={form.contactPerson.lastnameFirst}
                onChange={handleChange}
                error={!!errors.contactPerson.lastnameFirst}
                helperText={errors.contactPerson.lastnameFirst}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                fullWidth
                required
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                label="Segundo Apellido"
                name="contactPerson.lastnameSecond"
                value={form.contactPerson.lastnameSecond}
                onChange={handleChange}
                error={!!errors.contactPerson.lastnameSecond}
                helperText={errors.contactPerson.lastnameSecond}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                fullWidth
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                label="Email"
                name="contactPerson.email"
                error={!!errors.contactPerson.email}
                helperText={errors.contactPerson.email}
                value={form.contactPerson.email}
                onChange={handleChange}
                autoComplete="off"
                slotProps={{ htmlInput: { maxLength: 100 } }}
                fullWidth
                required
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                label="Teléfono móvil"
                name="contactPerson.mobileNumber"
                error={!!errors.contactPerson.mobileNumber}
                helperText={errors.contactPerson.mobileNumber}
                value={form.contactPerson.mobileNumber}
                onChange={handleChange}
                slotProps={{ htmlInput: { maxLength: 13 } }}
                required
                fullWidth
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 6, lg: 3 }}>
              <TextField
                label="Teléfono domicilio"
                name="contactPerson.phoneNumber"
                error={!!errors.contactPerson.phoneNumber}
                helperText={errors.contactPerson.phoneNumber}
                value={form.contactPerson.phoneNumber}
                onChange={handleChange}
                slotProps={{ htmlInput: { maxLength: 13 } }}
                fullWidth
              />
            </Grid>
          </Grid>
        );

      case 3: // informacion laboral
        return (
          <Grid container spacing={2}>

            <input type="text" name="fake_firstname" autoComplete="off" style={{ display: 'none' }} />
            <input type="text" name="fake_lastname" autoComplete="off" style={{ display: 'none' }} />
            <input type="text" name="fake_email" autoComplete="off" style={{ display: 'none' }} />

            <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
              <TextField
                label="Salario mensual (MXN)"
                name="salary"
                autoComplete="off"
                value={form.salary}
                onChange={handleChange}
                error={!!errors.salary}
                helperText={errors.salary}
                slotProps={{ htmlInput: { maxLength: 13 } }}
                fullWidth
                required
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
              <TextField
                label="RFC"
                name="rfc"
                autoComplete="off"
                value={form.rfc}
                onChange={handleChange}
                error={!!errors.rfc}
                helperText={errors.rfc}
                slotProps={{ htmlInput: { maxLength: 13 } }}
                fullWidth
                required
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
              <TextField
                autoComplete="new-password"
                label="CURP"
                name="curp"
                value={form.curp}
                onChange={handleChange}
                error={!!errors.curp}
                helperText={errors.curp}
                slotProps={{ htmlInput: { maxLength: 18 } }}
                fullWidth
                required
              />
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <form autoComplete="off">
      <Card>
        <CardHeader title={employee ? 'Editar Usuario' : 'Registro nuevo usuario'}
          titleTypographyProps={{ variant: 'h6' }} />
        <CardContent>
          <Tabs
            value={activeStep}
            onChange={(e, newValue) => setActiveStep(newValue)}
            variant="fullWidth"
            sx={{ mb: 3 }}
          >
            {steps.map((label, idx) => (
              <Tab key={idx} label={label} disabled={!isStepValid(idx - 1)} />
            ))}
          </Tabs>

          {renderStepContent(activeStep)}
        </CardContent>

        <CardActions sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>

          <Button
            onClick={onCancel}
            color="error">
            Cancelar
          </Button>

          <Box>
            {activeStep > 0 && (
              <Button
                onClick={() => setActiveStep(prev => prev - 1)}
                variant="outlined"
                sx={{
                  mr: 3,
                  color: "#364f6b",
                  "&:hover": {
                    backgroundColor: "#c3cbd4"
                  }
                }}>
                Atrás
              </Button>
            )}

            {activeStep < steps.length - 1 ? (
              <Button
                sx={{
                  color: "#364f6b",
                  "&:hover": {
                    backgroundColor: "#c3cbd4"
                  }
                }}
                variant="outlined"
                onClick={() => setActiveStep(prev => prev + 1)}
                disabled={!isStepValid()}
              >
                Siguiente
              </Button>
            ) : (
              <Button
                sx={{
                  backgroundColor: "#3f74b1",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#364f6b"
                  }
                }}
                variant="contained"
                onClick={handleSubmit}
                disabled={!isStepValid()}
              >
                Guardar
              </Button>
            )}
          </Box>
        </CardActions>
      </Card>
    </form>
  );
}
